/**
 * PATH: packages/auth/src/session-manager/tabIdentity.ts
 * FILE_ID: AUTH-TAB-IDENTITY-001
 * PURPOSE: Per-tab identity pin + server-header verifier. Closes the
 *          two robustness gaps qf!3238 left open:
 *            (a) `localStorage.quotech_last_user_id` is SHARED across
 *                tabs, so the cross-tab broadcast receiver could
 *                misread "is this for me?" after a sibling tab's
 *                login overwrote the key.
 *            (b) The silent-refresh `sub`-comparison guard only fires
 *                when the FE explicitly refreshes a token. A
 *                suspended tab whose bearer is still valid never
 *                triggers it, so a cookie-jar swap goes undetected
 *                until the next refresh — potentially after data has
 *                already leaked into the tab's React tree.
 * WHY:     The tab needs a PRIVATE source of truth for "what user
 *          does THIS TAB think it is?" — distinct from the cookie
 *          jar (shared by domain) and from localStorage (shared by
 *          origin). `sessionStorage` is per-tab by definition, set
 *          once at bootstrap, never overwritten by sibling tabs.
 * FLOW:
 *   ┌───────────────┐     ┌───────────────┐     ┌────────────────┐
 *   │ tab bootstrap │ ──► │ pin sessionSt │ ◄── │ first auth resp│
 *   └───────────────┘     │ "I am user X" │     │ X-User-Id: X   │
 *                         └───────────────┘     └────────────────┘
 *                                  │
 *                                  ▼
 *                    ┌───────────────────────────┐
 *                    │ every subsequent response │
 *                    │ verify X-User-Id == pin   │
 *                    │ mismatch → broadcast+reload│
 *                    └───────────────────────────┘
 * DEPENDENCIES:
 *   - tokenSync.ts (IdentityChangePayload, broadcastIdentityChange)
 * RELATED:
 *   - createApiAuthGate.tsx (calls pinTabIdentity on applySession)
 *   - src/main.jsx + apps/<tenant>/src/main.tsx (call pinTabIdentity at boot)
 *   - axios + fetch interceptors (call assertResponseIdentity)
 */

import { broadcastIdentityChange } from './tokenSync'

/** sessionStorage key for the per-tab identity pin. Distinct from
 *  `quotech_last_user_id` in localStorage (the SHARED one) so a
 *  search-and-replace bug can't merge the two by accident. */
export const TAB_IDENTITY_KEY = 'quotech_tab_user_id'

/** Converge-aware identity-mismatch reload one-shot — BUG-2026-06-22
 *  (auth-pipeline-loops family, durable cure). Value is the `${tab}>${server}`
 *  drift pair this tab has ALREADY reloaded for this session. A hard reload
 *  only resolves a TRANSIENT sibling-tab cookie swap (converges in one
 *  reload); a PERSISTENT cookie/bearer drift (two simultaneously-valid
 *  identities) never converges, so a second reload only strobes. Reload AT
 *  MOST ONCE per pair, then defer to the IdentityMismatchBanner / re-login.
 *  Shared byte-for-byte with tenant-shared quotechApi.ts (the fetch twin) so
 *  the axios + fetch clients coordinate to a single reload per drift. */
export const IDENTITY_RELOAD_ONESHOT_KEY = 'quotech_identity_reload_oneshot'

/** Server identity headers (lowercase for `Headers.get`, capitalised
 *  for the rare interceptor that takes a `Record<string,string>`). */
export const X_USER_ID_HEADER = 'x-user-id'
export const X_ORG_ID_HEADER = 'x-org-id'

/** Pin the tab's identity into sessionStorage. Idempotent in the
 *  "same user" case: if a pin already exists and matches, no-op. If
 *  the pin exists and DOES NOT match the incoming id, the caller is
 *  trying to swap identity inside a single tab lifecycle — log a
 *  warning, do NOT overwrite the pin (the response-header guard will
 *  catch the next request and reload).
 *
 *  Pass `null` / empty to clear (used by explicit logout paths).
 */
export function pinTabIdentity(userId: string | number | null | undefined): void {
  if (typeof sessionStorage === 'undefined') return
  const normalised = _normaliseId(userId)
  try {
    if (normalised === null) {
      sessionStorage.removeItem(TAB_IDENTITY_KEY)
      return
    }
    const existing = sessionStorage.getItem(TAB_IDENTITY_KEY)
    if (existing && existing !== normalised) {
      console.warn(
        '[@quotech/auth] pinTabIdentity called with a DIFFERENT user id '
        + `(existing=${existing}, incoming=${normalised}). The tab `
        + 'identity pin is sticky — broadcast + reload should handle '
        + 'identity transitions, NOT silent re-pinning.',
      )
      return
    }
    if (!existing) sessionStorage.setItem(TAB_IDENTITY_KEY, normalised)
  } catch {
    /* sessionStorage may throw on quota / private mode; identity
     * verification then falls back to "pin-missing" verdicts which
     * the interceptor handles by trusting the server header. */
  }
}

/** Read the per-tab identity pin. Returns null if not yet pinned. */
export function getTabIdentity(): string | null {
  if (typeof sessionStorage === 'undefined') return null
  try { return sessionStorage.getItem(TAB_IDENTITY_KEY) } catch { return null }
}

/** Test-only / explicit-logout reset. NOT for routine identity
 *  transitions — those should reload the tab so a fresh bootstrap
 *  re-pins from the new identity. */
export function clearTabIdentity(): void {
  if (typeof sessionStorage === 'undefined') return
  try { sessionStorage.removeItem(TAB_IDENTITY_KEY) } catch { /* noop */ }
}

/** Coerce mixed-type id inputs (number / string / email) to a stable
 *  lowercased-string for comparison. Mirrors `decodeJwtSub` semantics
 *  so the two identity sources (cookie JWT sub, response header) are
 *  compared apples-to-apples. */
function _normaliseId(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined) return null
  const s = String(value).trim().toLowerCase()
  return s ? s : null
}

/** Verdict from reconciling the tab pin against an AUTHORITATIVE
 *  identity (the live server session resolved by the auth gate). */
export type TabIdentityReconcileVerdict =
  /** Pin already absent/empty — set it (normal bootstrap). */
  | { kind: 'pin'; userId: string }
  /** Pin already matches the session — nothing to do. */
  | { kind: 'noop' }
  /** Pin disagrees with the session — the boot pin was stale (it is
   *  seeded from the sibling-mutable `quotech_last_user_id`); reset it
   *  to the authoritative id. */
  | { kind: 'reset'; from: string; to: string }

/**
 * PURE decision: given the CURRENT tab pin and an authoritative session
 * user id, decide how to reconcile them. No side effects — exported so
 * the branch logic is unit-testable without touching sessionStorage.
 *
 * Conservative: the legacy "email pin vs numeric-pk session" shape (one
 * contains '@', the other doesn't) is treated as a NOOP, not a reset —
 * that is the documented silent-migration case `handleResponseIdentity`
 * heals (email→pk), not a genuine wrong-user pin.
 *
 * @param existingPin  `getTabIdentity()` — what THIS tab is pinned to
 * @param incomingUserId the authoritative session user id (server pk)
 */
export function decideTabIdentityReconcile(
  existingPin: string | null,
  incomingUserId: string | number | null | undefined,
): TabIdentityReconcileVerdict {
  const incoming = _normaliseId(incomingUserId)
  if (incoming === null) return { kind: 'noop' }
  const pin = _normaliseId(existingPin)
  if (pin === null) return { kind: 'pin', userId: incoming }
  if (pin === incoming) return { kind: 'noop' }
  // Legacy email-pin vs numeric-pk session — not a real swap.
  if (pin.includes('@') !== incoming.includes('@')) return { kind: 'noop' }
  return { kind: 'reset', from: pin, to: incoming }
}

/**
 * Reconcile the per-tab pin against the AUTHORITATIVE session identity
 * the auth gate just resolved. Unlike `pinTabIdentity` (which is a
 * sticky no-op on a mismatch), this RESETS a stale pin to the live
 * session user.
 *
 * WHY: the boot pin (`main.tsx` / `main.jsx`) is seeded from the SHARED,
 * sibling-mutable `quotech_last_user_id`. A sibling tab's login can make
 * it wrong, so this tab can boot pinned to user 18 while its real
 * session is user 19. `pinTabIdentity(19)` then silently no-ops (sticky)
 * and the tab stays split-brained — every identity-scoped read (e.g. the
 * kanban email rail) comes back empty/wrong for the stale user. The auth
 * gate's session payload is the authoritative source of truth, so it is
 * allowed to overrule a stale boot pin.
 *
 * NO RELOAD on reset — deliberately. At auth-gate resolution the
 * protected React tree has not mounted yet, so there is nothing to tear
 * down, and a reload here would risk the reload-loop family
 * (BUG-2026-05-22-EVERY-SECOND-RELOAD-LOOP). A genuine MID-session
 * identity swap is still caught and reloaded by the response-header
 * guard (`handleResponseIdentity`), which fires on the server's
 * authoritative `X-User-Id`. Never throws.
 *
 * @returns the verdict so the caller can log structured telemetry.
 */
export function reconcileTabIdentityToSession(
  incomingUserId: string | number | null | undefined,
  options?: { reasonTag?: string },
): TabIdentityReconcileVerdict {
  if (typeof sessionStorage === 'undefined') return { kind: 'noop' }
  const verdict = decideTabIdentityReconcile(getTabIdentity(), incomingUserId)
  try {
    if (verdict.kind === 'pin') {
      sessionStorage.setItem(TAB_IDENTITY_KEY, verdict.userId)
    } else if (verdict.kind === 'reset') {
      sessionStorage.setItem(TAB_IDENTITY_KEY, verdict.to)
      if (typeof console !== 'undefined') {
        console.warn(
          '[@quotech/auth] Reconciled a stale tab identity pin '
          + `("${verdict.from}" → "${verdict.to}", ${options?.reasonTag ?? 'session'}). `
          + 'The boot pin (seeded from the shared quotech_last_user_id) '
          + 'was superseded by the authoritative live session identity.',
        )
      }
    }
  } catch {
    /* sessionStorage may throw on quota / private mode — identity
     * verification then falls back to the response-header guard. */
  }
  return verdict
}

/** Verdict from comparing a server-asserted identity to the tab pin. */
export type IdentityVerdict =
  /** Server X-User-Id matches the tab pin. Happy path. */
  | { kind: 'match'; userId: string }
  /** Tab not yet pinned. Pin the server's identity (bootstrap case). */
  | { kind: 'pin-on-first-response'; userId: string }
  /** Server didn't stamp a header. Harmless for public endpoints. */
  | { kind: 'server-silent'; tabUserId: string | null }
  /** Pin says user X, server says user Y. The cookie jar was swapped
   *  under us. Caller MUST broadcast identity-change and reload. */
  | { kind: 'mismatch'; tabUserId: string; serverUserId: string }

/** Read an `X-User-Id` value from a response and compare to the tab
 *  pin. Accepts either:
 *    - a `Headers` instance (fetch API)
 *    - a `{ get(name): string | null }` shape (axios `response.headers`)
 *    - a plain `Record<string, string | undefined>` (lower-case keys
 *      OR `X-User-Id` case — both are checked)
 *  Returns a verdict; never mutates state, never throws.
 */
export function assertResponseIdentity(
  headers: ResponseHeaderBag | null | undefined,
): IdentityVerdict {
  const serverUserId = _normaliseId(_readHeader(headers, X_USER_ID_HEADER))
  const tabUserId = getTabIdentity()

  if (serverUserId === null) {
    return { kind: 'server-silent', tabUserId }
  }
  if (tabUserId === null) {
    return { kind: 'pin-on-first-response', userId: serverUserId }
  }
  if (tabUserId === serverUserId) {
    return { kind: 'match', userId: serverUserId }
  }
  return { kind: 'mismatch', tabUserId, serverUserId }
}

/** Convenience: run `assertResponseIdentity` and, on a mismatch,
 *  broadcast identity-change + reload after a 150 ms grace.
 *
 *  Wires into every API client's response interceptor. Returns the
 *  verdict so callers can log it but does NOT throw on mismatch
 *  (the reload handles the recovery; throwing into the response
 *  pipeline would surface a stack trace to the user before the
 *  reload fires).
 */
export function handleResponseIdentity(
  headers: ResponseHeaderBag | null | undefined,
  options?: { reasonTag?: string },
): IdentityVerdict {
  const verdict = assertResponseIdentity(headers)
  if (verdict.kind === 'pin-on-first-response') {
    pinTabIdentity(verdict.userId)
  } else if (verdict.kind === 'mismatch') {
    // Legacy boot pins used email from localStorage while the BE
    // stamps Django pk — migrate silently instead of reloading.
    if (
      verdict.tabUserId.includes('@')
      && !verdict.serverUserId.includes('@')
    ) {
      try {
        sessionStorage.setItem(TAB_IDENTITY_KEY, verdict.serverUserId)
      } catch { /* noop */ }
      return { kind: 'match', userId: verdict.serverUserId }
    }
    const tag = options?.reasonTag ?? 'unknown-client'
    // BUG-2026-05-22-EVERY-SECOND-RELOAD-LOOP (fifth manifestation):
    //
    //   `setTimeout(reload, 150)` ALONE does NOT break the loop here,
    //   because:
    //     (a) `sessionStorage` SURVIVES `window.location.reload()` —
    //         the tab pin we read above (`verdict.tabUserId`) is
    //         STILL there post-reload. So if we leave it untouched,
    //         the very first response after the reload re-runs this
    //         exact comparison (pin="A", server="B"), hits the
    //         mismatch branch again, and reloads again. Loop.
    //     (b) `localStorage` is shared across tabs. A sibling tab may
    //         have written user A's bearer into `IdToken` (legacy
    //         key still used by axiosSetup). Post-reload the request
    //         interceptor attaches Bearer A → BE rejects → refresh →
    //         newSub=B → axiosSetup's sub-mismatch branch fires the
    //         same reload-without-cleanup pattern. Compounds the
    //         loop.
    //
    //   Operator-reported as "screen refreshing every 1 second" on
    //   `dashboard.quotech.ai/jobs` even AFTER the prior fixes that
    //   cleared `localStorage` in axiosSetup (55140d488) and the
    //   cross-tab receiver in `src/main.jsx` (0bb154dd6) shipped.
    //   Those fixes broke the SUB-mismatch reload route, but THIS
    //   response-header mismatch route still ran on every API call
    //   from the master SPA's axios interceptor (axiosSetup.ts:214,
    //   axiosSetup.ts:221) and reloaded with the pin intact.
    //
    //   Fix: before scheduling the reload, clear BOTH the legacy
    //   localStorage bearer keys (so post-reload requests go out
    //   with cookie auth only and the BE returns user B's data
    //   cleanly) AND the sessionStorage tab pin (so the post-reload
    //   bootstrap re-pins from the FIRST authenticated response
    //   under the new identity, not the stale pre-reload pin). Both
    //   clears together break the loop in a single reload.
    //
    //   We deliberately keep the broadcast + reload — the React
    //   tree built for user A must be torn down so cached components
    //   don't leak into user B's session. The reload is the safety
    //   recovery; the cleanups are what make the recovery converge.
    // Converge-aware reload (BUG-2026-06-22). Has this exact (tab→server)
    // drift already cost us a reload this session? sessionStorage survives
    // reloads, so seeing the same pair again means the reload did NOT converge
    // it → it is a persistent two-identity drift and a second reload would
    // only strobe (the strobe operators reported on the tenant SPAs).
    const driftPair = `${verdict.tabUserId}>${verdict.serverUserId}`
    let reloadedThisPair = false
    try {
      reloadedThisPair = sessionStorage.getItem(IDENTITY_RELOAD_ONESHOT_KEY) === driftPair
    } catch { /* private mode — treat as not-yet-tried */ }
    // Security action (ALWAYS): drop the stale bearer + tab pin so we stop
    // USING the wrong-identity credential, whether or not we reload.
    try {
      localStorage.removeItem('IdToken')
      localStorage.removeItem('accessTokennew')
      localStorage.removeItem('access_token')
      localStorage.removeItem('refreshToken')
    } catch { /* private mode / quota — ignore */ }
    try {
      sessionStorage.removeItem(TAB_IDENTITY_KEY)
    } catch { /* private mode / quota — ignore */ }
    console.warn(
      `[@quotech/auth] Response identity mismatch (client=${tag}). `
      + `Tab pinned to "${verdict.tabUserId}" but server stamped `
      + `"${verdict.serverUserId}". Cleared stale bearer + tab pin; `
      + (reloadedThisPair
        ? 'a reload already failed to converge this drift — NOT reloading again.'
        : 'broadcasting identity-change and reloading.'),
    )
    try {
      broadcastIdentityChange({
        userId: verdict.serverUserId,
        orgId: null,
        reason: `tabIdentity.handleResponseIdentity:${tag}`,
      })
    } catch { /* broadcast must never break the response pipeline */ }
    if (typeof window !== 'undefined') {
      if (reloadedThisPair) {
        // Persistent, non-converging drift: a reload already failed to
        // reconcile this exact pair this session. Stop — reloading two live
        // identities is futile and just strobes. The bearer/pin are cleared
        // (fail closed); the remedy is the IdentityMismatchBanner ("Switch
        // Account") or an explicit sign out + back in.
        console.error(
          `[@quotech/auth] persistent identity drift (tab="${verdict.tabUserId}" `
          + `server="${verdict.serverUserId}"): a full-page reload already failed to `
          + 'reconcile it this session, so NOT reloading again — this stops the strobe. '
          + 'Both identities are live; use "Switch Account" on the identity banner, or '
          + 'sign out and back in.',
        )
        return verdict
      }
      // First reload for this pair: record the one-shot BEFORE reloading (it
      // survives the reload) and converge a genuine transient swap in this
      // single reload.
      try { sessionStorage.setItem(IDENTITY_RELOAD_ONESHOT_KEY, driftPair) } catch { /* best effort */ }
      setTimeout(() => { window.location.reload() }, 150)
    }
  }
  return verdict
}

/** Loose-typed header-bag accessor that works for `Headers`, axios
 *  `response.headers` (which has `.get()` in axios v1+), and the
 *  plain-object shape some test helpers pass. */
export type ResponseHeaderBag =
  | Headers
  | { get(name: string): string | null }
  | Record<string, string | string[] | undefined | null>

function _readHeader(bag: ResponseHeaderBag | null | undefined, key: string): string | null {
  if (!bag) return null
  // Headers / axios — duck-type `.get`
  const maybeGet = (bag as { get?: unknown }).get
  if (typeof maybeGet === 'function') {
    try {
      const v = (bag as { get: (k: string) => string | null }).get(key)
        ?? (bag as { get: (k: string) => string | null }).get(key.toLowerCase())
      return _coerceHeaderValue(v)
    } catch { /* fall through to record path */ }
  }
  const rec = bag as Record<string, string | string[] | undefined | null>
  // axios v0.x exposed headers as a plain object with lowercase keys.
  const raw = rec[key] ?? rec[key.toLowerCase()] ?? rec[_capitaliseHyphenated(key)]
  return _coerceHeaderValue(raw)
}

function _coerceHeaderValue(v: string | string[] | null | undefined): string | null {
  if (v === null || v === undefined) return null
  if (Array.isArray(v)) return v.length > 0 ? String(v[0]) : null
  return String(v)
}

function _capitaliseHyphenated(key: string): string {
  return key.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join('-')
}
